import { type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Tidecaller (the Abyssal Angler set's gun, ADR-0160 amended): a harpoon cannon of riveted iron bound in brass, a
 * porthole in its side with the sea sloshing in it (a speck of light swimming there, a bubble rising), a vent on its
 * back, a barbed harpoon in its mouth. Its sea is green, never the team's blue (a held gun isn't recoloured by team).
 */

const BRASS = ['n', 'U', 'Y', 'y'] as const;
const IRON = ['1', '2', '3', '4'] as const;
/** The porthole's middle, its glass's radius and its bezel's. */
const [PX, PY, GLASS, RIM] = [33, 19.5, 4.4, 6.3] as const;
/** The sea in the porthole per frame: how its surface tilts, the light swimming in it, a bubble rising. */
const SLOSH = [0.45, 0.15, -0.45, -0.15] as const;
const PLANKTON = [
  [30, 22],
  [31, 21],
  [33, 22],
  [32, 21],
] as const;
const BUBBLES = [
  [35, 23],
  [35, 21],
  [36, 20],
  [34, 23],
] as const;

/** The barrel's mouth: the harpoon shows from here on. */
const MOUTH = 52;

/** The harpoon, its point at `tip` (x): a steel head narrowing to it, two barbs swept back, its shaft behind. */
function harpoon(c: PixelCanvas, tip: number): void {
  const back = tip - 7;
  for (let x = MOUTH; x < back; x++) c.set(x, 19, '4').set(x, 20, '3');
  for (let x = Math.max(MOUTH, back); x <= tip; x++) {
    const half = (3.6 * (tip + 0.5 - x)) / 7.5;
    for (let y = 14; y <= 25; y++) {
      const d = y + 0.5 - PY;
      if (Math.abs(d) >= half) continue;
      c.set(x, y, d < -half + 1 ? 'w' : d < 0 ? '5' : d < half - 1 ? '4' : '3');
    }
  }
  // The barbs, swept back from the head's corners.
  for (let k = 0; k < 3; k++) {
    if (back - k < MOUTH) continue;
    c.set(back - k, 16 - k, '5').set(back - k, 17 - k, '4');
    c.set(back - k, 22 + k, '4').set(back - k, 23 + k, '3');
  }
}

const tidecaller = (frame: number, tip = 62) =>
  outlined((c) => {
    // The stock: iron, a brass butt cap, two rivets.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, IRON, 1, 2), 'l');
    box(c, 5, 17, 7, 24, [1], (_x, y) => (y < 19 ? 'y' : y > 22 ? 'U' : 'Y'), 'l');
    dots(c, '5', [
      [12, 19],
      [18, 19],
    ]);
    // The body: riveted iron, lit along its top, bound in brass at its back, its front a cone narrowing into the barrel.
    box(c, 22, 13, 42, 26, [2, 1], byRow(13, 26, IRON, 1, 3), 'l');
    taper(c, 43, 47, 19.5, 6.6, 3.8, round(IRON));
    for (const x of [23, 24]) taper(c, x, x, 19.5, 6.3, 6.3, round(BRASS));
    for (let x = 27; x <= 43; x += 4) c.set(x, 14, 'Y');
    // The vent on its back, where the bubbles come out.
    box(c, 36, 10, 38, 12, [], (x, y) => (y === 10 ? 'y' : x === 36 ? 'Y' : x === 38 ? 'n' : 'U'));
    // The porthole: the sea sloshing behind glass in a bolted bezel.
    for (let y = 12; y <= 27; y++)
      for (let x = 25; x <= 41; x++) {
        const [dx, dy] = [x + 0.5 - PX, y + 0.5 - PY];
        const r = Math.hypot(dx, dy);
        if (r > RIM) continue;
        if (r > GLASS) {
          // A groove round the bezel parts it from the body; the bezel lit on its top-left, bolted.
          const lit = -(dx + dy) / r;
          const bolt = Math.abs(Math.abs(dx) - Math.abs(dy)) < 1 && r > GLASS + 0.6 && r < RIM - 1;
          if (r > RIM - 0.9) c.set(x, y, lit > 0.3 ? 'U' : 'n');
          else c.set(x, y, bolt ? (dx + dy < 0 ? 'e' : 'y') : lit > 0.4 ? 'y' : lit > -0.4 ? 'Y' : 'U');
          continue;
        }
        const surface = PY - 0.6 + (SLOSH[frame] ?? 0) * dx;
        if (y + 0.5 < surface) {
          // Air behind the glass, a reflection curving round its top-left.
          const a = Math.atan2(dy, dx);
          c.set(x, y, r > GLASS - 1.6 && a > -2.6 && a < -1.5 ? 'w' : '1');
        } else c.set(x, y, y + 0.5 < surface + 1 ? 'l' : r > GLASS - 1.2 && dx + dy > 0 ? 'D' : 'G');
      }
    const [lx, ly] = PLANKTON[frame] ?? PLANKTON[0];
    c.set(lx, ly, 'y').set(lx + 1, ly, 'l');
    const [bx, by] = BUBBLES[frame] ?? BUBBLES[0];
    c.set(bx, by, 'w');
    // The trigger.
    box(c, 30, 27, 33, 29, [0, 1], (_x, y) => (y === 27 ? 'Y' : 'U'), 'r');
    // The barrel: iron, bound in brass at both ends, the harpoon's shaft in it.
    taper(c, 48, MOUTH - 1, 19.5, 3.4, 3, round(IRON));
    for (const x of [48, MOUTH - 1]) taper(c, x, x, 19.5, 3.6, 3.6, round(['N', 'n', 'U', 'Y']));
    if (tip > 0) harpoon(c, tip);
    // Fired: the mouth empty, dark inside.
    else
      dots(c, 'N', [
        [MOUTH - 1, 19],
        [MOUTH - 1, 20],
      ]);
  });

const frames = [tidecaller(0), tidecaller(1), tidecaller(2), tidecaller(3)];

export default sprite({
  name: 'pollinator_angler',
  ...geometry,
  notes: `Tidecaller (ADR-0160 amended, the Abyssal Angler set's gun): a harpoon cannon of riveted iron (dark beside the
brass helm) bound in brass, an iron stock with a brass cap, a porthole in its side (a grooved bolted bezel, green sea
sloshing behind the glass over 4 frames, a speck of light swimming in it, a bubble rising), a brass vent on its back
(its flair: bubbles), its front a cone into a short bound barrel, a barbed steel harpoon in its mouth that flies on each
shot and slides back in (fire). Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the iron body is flat at 1:1; the porthole's
sea is 8 px, a green dot past the fist.`,
  anims: {
    idle: { fps: 6, frames },
    fire: { fps: 24, loop: false, frames: [tidecaller(1, 0), tidecaller(2, 56), tidecaller(3, 60), tidecaller(0)] },
  },
});
